/**
 * Office.js Word helper — stays add-in specific.
 * All Word document interactions go through this module.
 * Types provided by @types/office-js.
 */

export interface ContentControlTag {
  tag: string;
  title: string;
  type: string;
  currentValue: string;
}

/**
 * Get all content control tags from the current document.
 */
export async function getContentControlTags(): Promise<ContentControlTag[]> {
  const tags: ContentControlTag[] = [];

  await Word.run(async (context) => {
    const controls = context.document.body.contentControls;
    controls.load('tag,title,type,text');
    await context.sync();

    for (const ctrl of controls.items) {
      if (ctrl.tag) {
        tags.push({
          tag: ctrl.tag,
          title: ctrl.title || ctrl.tag,
          type: ctrl.type,
          currentValue: ctrl.text,
        });
      }
    }
  });

  return tags;
}

/**
 * Get all {tag} plain-text tokens in the document — including those inside
 * floating text boxes (which aren't content controls). Uses the body's OOXML
 * which contains all descendant text regardless of container type.
 */
export async function getTextTokenTags(): Promise<ContentControlTag[]> {
  const tags: ContentControlTag[] = [];
  const seen = new Set<string>();

  await Word.run(async (context) => {
    const body = context.document.body;
    const ooxmlResult = body.getOoxml();
    await context.sync();

    const ooxml = ooxmlResult.value || '';
    // Strip XML tags to get flat text, then scan for {tag} patterns.
    // Alternative: regex the raw OOXML — but text can be split across <w:t>
    // elements, so flattening first is more reliable.
    const flat = ooxml.replace(/<[^>]+>/g, '');
    const matches = flat.matchAll(/\{([a-zA-Z_][a-zA-Z0-9_.]*)\}/g);

    for (const m of matches) {
      const tag = m[1];
      if (seen.has(tag)) continue;
      seen.add(tag);
      tags.push({
        tag,
        title: tag,
        type: 'Text',
        currentValue: `{${tag}}`,
      });
    }
  });

  return tags;
}

/**
 * Get all placeholder tags in the document — union of content controls + plain
 * text tokens (including floating text box contents).
 */
export async function getAllPlaceholderTags(): Promise<ContentControlTag[]> {
  const [cc, txt] = await Promise.all([getContentControlTags(), getTextTokenTags()]);
  const seen = new Set<string>(cc.map(t => t.tag));
  const merged = [...cc];
  for (const t of txt) {
    if (!seen.has(t.tag)) {
      seen.add(t.tag);
      merged.push(t);
    }
  }
  return merged;
}

/**
 * Fill content controls with provided data.
 */
export async function fillContentControls(data: Record<string, string>): Promise<number> {
  let filledCount = 0;

  await Word.run(async (context) => {
    const controls = context.document.body.contentControls;
    controls.load('tag,text');
    await context.sync();

    for (const ctrl of controls.items) {
      if (ctrl.tag && ctrl.tag in data) {
        ctrl.insertText(data[ctrl.tag], 'Replace');
        filledCount++;
      }
    }

    await context.sync();
  });

  return filledCount;
}

/**
 * Get unique tag names (deduplicated).
 */
export async function getUniqueTags(): Promise<string[]> {
  const tags = await getContentControlTags();
  const unique = new Set(tags.map(t => t.tag));
  return [...unique].sort();
}

/**
 * Categorize tags by their prefix convention.
 */
export function categorizeTags(tags: string[]): {
  simple: string[];
  conditional: string[];
  repeat: string[];
} {
  const simple: string[] = [];
  const conditional: string[] = [];
  const repeat: string[] = [];

  for (const tag of tags) {
    if (tag.startsWith('IF:') || tag.startsWith('UNLESS:')) {
      conditional.push(tag);
    } else if (tag.startsWith('REPEAT:')) {
      repeat.push(tag);
    } else {
      simple.push(tag);
    }
  }

  return { simple, conditional, repeat };
}

// Insert a new RichText content control at cursor position
export async function insertContentControl(tag: string, title: string, placeholderText?: string): Promise<void> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    const cc = selection.insertContentControl(Word.ContentControlType.richText);
    cc.tag = tag;
    cc.title = title || tag;
    if (placeholderText) {
      cc.placeholderText = placeholderText;
    }
    cc.appearance = Word.ContentControlAppearance.boundingBox;
    await context.sync();
  });
}

// Insert a FLOATING text box containing {tag}. The text box sits on top of the
// document (wrap style = in front of text), so it doesn't disturb the text flow.
// User can drag the text box in Word to position it anywhere on the page —
// DocuSign-style placement within Word's native surface.
//
// Implementation: Office.js doesn't expose a direct "insert text box" API in
// stable types, so we inject OOXML that creates a VML text box with absolute
// position and "in front of text" wrapping. Positioned at a default (100pt,
// 100pt) on the page; user drags it in Word to the exact spot.
// Baseline offset applied when dropping a chip into a paragraph at (0,0). A
// raw paragraph-relative top of 0pt sits at the paragraph's ASCENDER line —
// visibly above the baseline where tab leaders/underscores live. Nudging down
// by this amount lands the chip on the underline for body paragraphs using
// Times New Roman 11pt. Authors can fine-tune per-chip with arrow keys after
// insert.
const BASELINE_OFFSET_PT = 3;

// Color theme per placeholder type — at-a-glance distinction for authors.
// Normalize the incoming type string (case-insensitive) to pick a palette.
// `fill` and `border` only render during authoring — the filler strips them
// for generated output.
const PLACEHOLDER_COLORS: Record<string, { fill: string; border: string; text: string }> = {
  text:    { fill: 'FFF4CC', border: 'E8C547', text: '5A4A00' }, // yellow
  date:    { fill: 'DCEBFF', border: '5B8DEF', text: '1F4B8E' }, // blue
  money:   { fill: 'D9F0DA', border: '4CAF50', text: '1F5F24' }, // green
  number:  { fill: 'D9F0DA', border: '4CAF50', text: '1F5F24' }, // green
  boolean: { fill: 'ECE0FA', border: '7B5FA0', text: '3F2560' }, // purple
  address: { fill: 'FFE5D4', border: 'E07C3C', text: '6B3510' }, // orange
};

function pickColors(type?: string) {
  const key = (type || 'text').toLowerCase();
  return PLACEHOLDER_COLORS[key] ?? PLACEHOLDER_COLORS.text;
}

export async function insertFloatingPlaceholder(tag: string, title: string, type?: string): Promise<void> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    const paragraph = selection.paragraphs.getFirst();
    // Paragraph-anchored DrawingML chip: lands on the cursor's paragraph at
    // the text column's left edge (horizontal) and the run's line (vertical).
    // We tried various marker-and-splice strategies to land the chip at the
    // cursor's exact character position, but Word's OOXML serialization is
    // too inconsistent across builds — markers get split across <w:t>
    // elements, leak into the document, etc. Paragraph-anchored insertion
    // is reliable on every Word build; the user drags the chip horizontally
    // to the precise spot after insert.
    const shapeOoxml = buildFloatingShapeOoxml(tag, title, type, { left: 0, top: BASELINE_OFFSET_PT, relative: 'paragraph' });
    paragraph.insertOoxml(shapeOoxml, Word.InsertLocation.end);
    await context.sync();

    // Auto-select-after-insert intentionally disabled. Calling
    // selectLastInsertedShape() right after a chip insert puts Word into a
    // state where the just-inserted chip renders without its {tag} text in
    // the textbox — the box draws correctly but the inner text is hidden
    // until the user clicks somewhere else / re-renders the page. The
    // selection convenience isn't worth losing chip readability. Users who
    // need to drag a chip can click it manually.
    // (selectLastInsertedShape function kept below in case we want to
    //  surface it as an explicit "Select last chip" action later.)
  });
}

/**
 * Locate the most-recently-inserted shape in the document body and try to
 * select it. Falls back to selecting the shape's text range when direct
 * shape selection isn't supported on the current Word build. Silent no-op
 * if neither approach works (e.g. very old Word desktop where the Shape
 * API isn't exposed at all).
 */
async function selectLastInsertedShape(context: Word.RequestContext): Promise<void> {
  try {
    const body = context.document.body as any;
    const shapes = body.shapes;
    if (!shapes) return;
    shapes.load('items/$none'); // load the collection itself, not item props
    await context.sync();
    const items = shapes.items as any[];
    if (!items || items.length === 0) return;
    const last = items[items.length - 1];
    // Path 1: direct .select() on the shape — works on Word desktop with
    // WordApi 1.6+, surfaces resize handles + anchor icon.
    if (typeof last.select === 'function') {
      try {
        last.select();
        await context.sync();
        return;
      } catch { /* fall through to range-based path */ }
    }
    // Path 2: shape.getRange().select() — moves the document selection into
    // the shape's text range. Word for the web exposes this even when
    // direct shape selection is blocked.
    if (typeof last.getRange === 'function') {
      try {
        const range = last.getRange();
        range.select();
        await context.sync();
      } catch { /* nothing more to try */ }
    }
  } catch { /* shapes API unsupported on this Word build */ }
}

// Build the VML floating-text-box OOXML for a placeholder.
//   relative: 'page'      — pinned to page coordinates (Enter in body text
//                           doesn't shift it). Used by the Insert button.
//   relative: 'paragraph' — anchored to the paragraph it's inserted into, so
//                           it sits at the drop location and travels with that
//                           line. Used when converting dropped text.
function buildFloatingShapeOoxml(
  tag: string,
  title: string,
  type: string | undefined,
  pos: { left: number; top: number; relative: 'page' | 'paragraph' },
): string {
  const displayText = escapeXml(`{${tag}}`);
  const shapeTitle = escapeXml(title || tag);
  const colors = pickColors(type);
  // Page-relative: absolute page coords (Insert button flow).
  // Paragraph-relative: anchored to the host paragraph; sits at the drop
  // location and travels with that line.
  //
  // This OOXML is the version that renders correctly in Word AND in
  // third-party PDF renderers (LibreOffice, Spire). An earlier rewrite that
  // used mso-position-horizontal-relative:char + :line for paragraph mode
  // broke layout in those renderers — chips ended up at wrong positions and
  // surrounding text reflowed weirdly. Don't add char/line relative anchors
  // here; the simple page-or-page-default behaviour is what works.
  const styleExtras = pos.relative === 'page'
    ? 'mso-position-horizontal:absolute;mso-position-horizontal-relative:page;mso-position-vertical:absolute;mso-position-vertical-relative:page'
    : '';
  return `
    <?xml version="1.0" encoding="UTF-8" standalone="yes"?>
    <pkg:package xmlns:pkg="http://schemas.microsoft.com/office/2006/xmlPackage">
      <pkg:part pkg:name="/_rels/.rels" pkg:contentType="application/vnd.openxmlformats-package.relationships+xml">
        <pkg:xmlData>
          <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
            <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
          </Relationships>
        </pkg:xmlData>
      </pkg:part>
      <pkg:part pkg:name="/word/document.xml" pkg:contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml">
        <pkg:xmlData>
          <w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
                      xmlns:v="urn:schemas-microsoft-com:vml"
                      xmlns:o="urn:schemas-microsoft-com:office:office"
                      xmlns:w10="urn:schemas-microsoft-com:office:word">
            <w:body>
              <w:p>
                <w:r>
                  <w:pict>
                    <v:roundrect id="placeholder_${Date.now()}_${Math.floor(Math.random() * 10000)}"
                                 arcsize="0.25"
                                 fillcolor="#${colors.fill}"
                                 strokecolor="#${colors.border}"
                                 strokeweight="0.75pt"
                                 style="position:absolute;margin-left:${pos.left}pt;margin-top:${pos.top}pt;width:80pt;height:16pt;z-index:251660288${styleExtras ? ';' + styleExtras : ''}"
                                 alt="${shapeTitle}">
                      <v:textbox style="mso-fit-shape-to-text:t" inset="2pt,0pt,2pt,0pt">
                        <w:txbxContent>
                          <w:p>
                            <w:pPr>
                              <w:spacing w:before="0" w:after="0" w:line="240" w:lineRule="auto"/>
                              <w:jc w:val="left"/>
                            </w:pPr>
                            <w:r>
                              <w:rPr>
                                <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman" w:cs="Times New Roman"/>
                                <w:color w:val="${colors.text}"/>
                                <w:sz w:val="22"/>
                                <w:szCs w:val="22"/>
                              </w:rPr>
                              <w:t xml:space="preserve">${displayText}</w:t>
                            </w:r>
                          </w:p>
                        </w:txbxContent>
                      </v:textbox>
                      <w10:wrap type="none"/>
                      <w10:anchorlock/>
                    </v:roundrect>
                  </w:pict>
                </w:r>
              </w:p>
            </w:body>
          </w:document>
        </pkg:xmlData>
      </pkg:part>
    </pkg:package>
  `.trim();
}

// Scan the body for plain-text {tag} tokens that were dropped via HTML5
// drag-and-drop (Word inserts them as inline text) and swap each one for a
// floating chip anchored at that text's position. Skips tokens already
// inside a shape/text box. Returns the number of conversions performed.
//
// `expectedToken` (optional) limits the search to a specific {tag} string,
// which lets the caller poll right after a drop without sweeping the whole
// body each tick — drastically tighter than a fixed-delay sweep and
// eliminates the visible {tag} → chip flash users were seeing.
export async function convertDroppedPlaceholders(
  typeMap: Record<string, string> = {},
  expectedToken?: string,
): Promise<number> {
  return Word.run(async (context) => {
    const body = context.document.body;
    // When polling for a specific token, search literally; otherwise fall
    // back to the wildcard sweep that catches any drop.
    const results = expectedToken
      ? body.search(expectedToken, { matchCase: true })
      : body.search('\\{[A-Za-z_][A-Za-z0-9_. \\-]@\\}', { matchWildcards: true });
    results.load('items');
    await context.sync();

    // Read each match's text up front (sync once for all of them).
    const ranges = results.items;
    for (const r of ranges) r.load(['text']);
    await context.sync();

    // Replace each {tag} text range with a floating DrawingML chip via
    // range.insertOoxml(replace). Letting Word's own range API do the splice
    // sidesteps the OOXML-string parsing quirks (proofErr wrappers, run
    // splitting across multiple <w:t> elements, tab leaders) that broke our
    // earlier regex-based splicer.
    let converted = 0;
    for (const range of ranges) {
      const text = range.text || '';
      const m = text.match(/^\{([A-Za-z_][A-Za-z0-9_. \-]*)\}$/);
      if (!m) continue;
      const tag = m[1];
      const type = typeMap[tag];
      // VML chip anchored to the matched text's paragraph. Word splices
      // the floating shape into the matched range — the {tag} text is
      // replaced by the chip in the paragraph's flow.
      const shapeOoxml = buildFloatingShapeOoxml(tag, tag, type, { left: 0, top: BASELINE_OFFSET_PT, relative: 'paragraph' });
      try {
        range.insertOoxml(shapeOoxml, Word.InsertLocation.replace);
        converted++;
      } catch {
        // Range refused the replace (rare — usually means the range is
        // inside an unsupported container). Skip; the plain {tag} text
        // remains and the doc-service will still substitute it at
        // generate time.
      }
    }
    await context.sync();
    return converted;
  });
}

// Splice a pict-run XML snippet into a paragraph's OOXML at the position where
// `token` appears inside a text run. The containing run is split into a
// before-text run, the pict run, and an after-text run (either side may be
// empty). Returns the paragraph unchanged if the token isn't located.
function injectPictIntoParagraph(paraXml: string, token: string, pictRun: string): string {
  // Find a <w:r> whose <w:t> contains the token. Capture: run-open attrs,
  // any pre-text content (rPr, proofErr, etc.), w:t open tag, before-token
  // text, after-token text, and run-close.
  //
  // The pre-text and post-text spans use a negative lookahead `(?!</w:r>)`
  // so they can't cross a run boundary. This tolerates spell-check
  // <w:proofErr> elements that Word inserts around unknown words like our
  // marker — the original strict-rPr-only regex failed there.
  const tokenEscaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const runRegex = new RegExp(
    '(<w:r\\b[^>]*>)' +                          // 1: run open
    '((?:(?!</w:r>)[\\s\\S])*?)' +               // 2: pre-text (rPr, proofErr, …)
    '(<w:t\\b[^>]*>)' +                          // 3: t open
    '([\\s\\S]*?)' +                             // 4: before-token text
    tokenEscaped +                                //    token
    '([\\s\\S]*?)' +                             // 5: after-token text
    '(</w:t>' +                                   // 6: run-close (w:t close
    '(?:(?!</w:r>)[\\s\\S])*?' +                  //     + any trailers
    '</w:r>)',                                   //     + actual r close)
  );
  const m = paraXml.match(runRegex);
  if (!m) return paraXml;
  const [full, runOpen, preText, tOpen, before, after, runClose] = m;
  // Strip spell-check proofErr elements — they targeted the marker text
  // and would be stale (and could appear duplicated across the split runs)
  // if we kept them. Anything else in pre/post (rPr, etc.) is preserved.
  const stripProofErr = (s: string) => s.replace(/<w:proofErr\b[^>]*\/?>(?:<\/w:proofErr>)?/g, '');
  const cleanPre = stripProofErr(preText);
  const cleanClose = stripProofErr(runClose);
  const makeRun = (text: string) =>
    text ? `${runOpen}${cleanPre}${tOpen.replace('<w:t', '<w:t xml:space="preserve"')}${text}${cleanClose}` : '';
  const replacement = makeRun(before) + pictRun + makeRun(after);
  return paraXml.replace(full, replacement);
}

// Re-skin every existing floating placeholder in the body to match the current
// color scheme (based on each tag's type from the catalog). Handles both
// legacy VML (<v:roundrect>) and modern DrawingML (<wps:wsp>) chips —
// each is replaced with a chip of the SAME format so we never accidentally
// alter the structural layout of existing templates. Preserves each shape's
// position from the source OOXML.
export async function restyleAllPlaceholders(typeMap: Record<string, string> = {}): Promise<number> {
  return Word.run(async (context) => {
    const body = context.document.body;
    const ooxmlResult = body.getOoxml();
    await context.sync();
    const original = ooxmlResult.value || '';

    let count = 0;

    // 1) Legacy VML chips — keep VML on rewrite (don't migrate format on a
    //    restyle action; a restyle should only change colours).
    const vmlRegex = /<v:(shape|roundrect)\b[^>]*>[\s\S]*?<\/v:\1>/g;
    let rebuilt = original.replace(vmlRegex, (match) => {
      const tagMatch = match.match(/<w:t[^>]*>\s*\{([A-Za-z_][A-Za-z0-9_.]*)\}\s*<\/w:t>/);
      if (!tagMatch) return match;
      const tag = tagMatch[1];
      const type = typeMap[tag];

      const styleMatch = match.match(/style="([^"]*)"/);
      const style = styleMatch ? styleMatch[1] : '';
      const leftMatch = style.match(/margin-left:(-?[\d.]+)pt/);
      const topMatch = style.match(/margin-top:(-?[\d.]+)pt/);
      const left = leftMatch ? parseFloat(leftMatch[1]) : 100;
      const top = topMatch ? parseFloat(topMatch[1]) : 100;
      const relative: 'page' | 'paragraph' = style.includes('mso-position-vertical-relative:page')
        ? 'page'
        : 'paragraph';

      count++;
      return extractShapeFragment(buildFloatingShapeOoxml(tag, tag, type, { left, top, relative }));
    });

    // 2) DrawingML chips — left untouched on restyle. They existed in
    //    templates saved during the DrawingML era. Re-saving the template
    //    through the add-in re-emits chips as VML, so legacy DrawingML
    //    chips are migrated naturally on next save. Rewriting them in
    //    place would risk partial-rewrite hazards (mismatched OOXML if a
    //    chip's structure differs from what we expect).

    if (count > 0) {
      body.insertOoxml(rebuilt, Word.InsertLocation.replace);
      await context.sync();
    }
    return count;
  });
}

// Pull just the <v:roundrect>...</v:roundrect> fragment out of the pkg:package
// document produced by buildFloatingShapeOoxml, for in-body string splicing.
function extractShapeFragment(ooxml: string): string {
  const m = ooxml.match(/<v:(shape|roundrect)\b[\s\S]*?<\/v:\1>/);
  return m ? m[0] : ooxml;
}

// Insert a floating placeholder chip into the same paragraph as the first
// occurrence of `searchText`. THIS FUNCTION NEVER DELETES OR REPLACES TEXT
// IN THE DOCUMENT — it only adds a chip. The matched text is used solely
// to locate the right paragraph; the chip is appended as a floating sibling
// inside that paragraph and sits on top of the line via wrapNone.
//
// Returns true when a match is found and a chip was added; false when the
// match wasn't found or the insert failed.
//
// Position strategy: target the underscore blank in the same paragraph if
// one exists (BOND NO. _____ → chip lands on the blank, label preserved),
// else target the matched text's position. Horizontal offset is estimated
// from the paragraph's run font and size — read at runtime so each
// template's actual font choice is respected. Underscores are ALWAYS kept
// in the document; the chip simply floats on top of them.
export async function insertFloatingPlaceholderAtMatch(
  searchText: string,
  tag: string,
  title: string,
  type?: string,
): Promise<boolean> {
  return Word.run(async (context) => {
    const body = context.document.body;
    const results = body.search(searchText, { matchCase: true, matchWholeWord: false });
    results.load('items');
    await context.sync();

    if (results.items.length === 0) return false;

    try {
      const matchedRange = results.items[0];
      const paragraph = matchedRange.paragraphs.getFirst();

      // Read paragraph text + matched range's font so we can estimate the
      // chip's horizontal position. Loading both in one sync.
      paragraph.load(['text']);
      matchedRange.load(['text']);
      matchedRange.font.load(['size', 'name']);
      await context.sync();

      const paraText = paragraph.text || '';
      const matchText = matchedRange.text || '';
      const fontSize = (matchedRange.font.size as number) || 11;
      const fontName = (matchedRange.font.name as string) || 'Times New Roman';

      // Prefer the position of an underscore blank (the form-line slot),
      // fall back to the matched text's position. We're always pointing the
      // chip at "where the value will visually go".
      const blankMatch = paraText.match(/_{2,}/);
      const targetIndex = blankMatch
        ? paraText.indexOf(blankMatch[0])
        : Math.max(0, paraText.indexOf(matchText));

      const prefix = paraText.substring(0, targetIndex);
      const offsetPt = estimateHorizontalOffsetPt(prefix, fontName, fontSize);

      const shapeOoxml = buildFloatingShapeOoxml(tag, title, type, {
        left: offsetPt,
        top: BASELINE_OFFSET_PT,
        relative: 'paragraph',
      });
      paragraph.insertOoxml(shapeOoxml, Word.InsertLocation.end);
      await context.sync();
      return true;
    } catch {
      return false;
    }
  });
}

// Average character-width ratio per font (× font-size = avg char width in pt).
// Approximate; proportional fonts have variable char widths so this only gets
// us "in the right neighbourhood". Used by insertFloatingPlaceholderAtMatch
// to estimate the chip's horizontal offset from a paragraph's left edge.
const FONT_WIDTH_RATIOS: Record<string, number> = {
  'Times New Roman': 0.50,
  'Times':           0.50,
  'Cambria':         0.50,
  'Georgia':         0.55,
  'Arial':           0.55,
  'Helvetica':       0.55,
  'Calibri':         0.50,
  'Verdana':         0.60,
  'Courier New':     0.60,
  'Courier':         0.60,
};

function estimateHorizontalOffsetPt(prefix: string, fontName: string, fontSize: number): number {
  if (!prefix) return 0;
  const ratio = FONT_WIDTH_RATIOS[fontName] ?? 0.50; // serif default
  const charWidth = fontSize * ratio;
  // Word's default tab stop is 0.5" = 36pt; close enough for most templates.
  const tabCount = (prefix.match(/\t/g) || []).length;
  const charCount = prefix.replace(/\t/g, '').length;
  return charCount * charWidth + tabCount * 36;
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// ── DrawingML chip authoring removed ──────────────────────────────────────
//
// Earlier this file contained `buildFloatingShapeOoxmlDrawing` and friends
// which emitted DrawingML floating shapes. They rendered correctly only in
// Word native; LibreOffice and Spire mis-rendered them, breaking the diff
// view and any PDF preview. All chip authoring now goes through the VML
// `buildFloatingShapeOoxml` above. Legacy DrawingML chips already in saved
// templates are still recognised on the server side (PlaceholderFiller
// handles both formats) and migrate to VML on next save through the add-in.

// Wrap current selection in a content control
export async function wrapSelectionInContentControl(tag: string, title: string): Promise<void> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    const cc = selection.insertContentControl(Word.ContentControlType.richText);
    cc.tag = tag;
    cc.title = title || tag;
    cc.appearance = Word.ContentControlAppearance.boundingBox;
    await context.sync();
  });
}

// Insert a repeating section placeholder
export async function insertRepeatingSection(tag: string, title: string): Promise<void> {
  const fullTag = tag.startsWith('REPEAT:') ? tag : `REPEAT:${tag}`;
  return insertContentControl(fullTag, title || fullTag, `[Repeating: ${tag}]`);
}

// Insert a conditional fragment (IF: or UNLESS:)
export async function insertConditionalFragment(condition: string, type: 'IF' | 'UNLESS'): Promise<void> {
  const tag = `${type}:${condition}`;
  return wrapSelectionInContentControl(tag, tag);
}

// Get document as base64 for export/save
export async function getDocumentAsBase64(): Promise<string> {
  // Save the document first so getFileAsync returns current content
  await Word.run(async (context) => {
    context.document.save();
    await context.sync();
  });

  return new Promise((resolve, reject) => {
    try {
      Office.context.document.getFileAsync(
        Office.FileType.Compressed,
        { sliceSize: 65536 },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            reject(new Error(result.error.message));
            return;
          }
          const file = result.value;
          const sliceCount = file.sliceCount;
          const slices: string[] = [];
          let slicesReceived = 0;

          if (sliceCount === 0) {
            file.closeAsync();
            resolve('');
            return;
          }

          function getSlice(index: number) {
            file.getSliceAsync(index, (sliceResult) => {
              if (sliceResult.status === Office.AsyncResultStatus.Failed) {
                file.closeAsync();
                reject(new Error(sliceResult.error.message));
                return;
              }
              slices[index] = sliceResult.value.data;
              slicesReceived++;

              if (slicesReceived === sliceCount) {
                file.closeAsync();
                // Join slices and encode as base64 (matches POC approach)
                resolve(btoa(slices.join('')));
              } else {
                getSlice(index + 1);
              }
            });
          }
          getSlice(0);
        }
      );
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
}

/**
 * Get the current selection as base64-encoded OOXML.
 */
export async function getSelectionAsBase64(): Promise<string> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    const ooxml = selection.getOoxml();
    await context.sync();
    // Encode OOXML as base64
    const encoder = new TextEncoder();
    const bytes = encoder.encode(ooxml.value);
    const CHUNK = 8192;
    let binary = '';
    for (let i = 0; i < bytes.length; i += CHUNK) {
      binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
    }
    return btoa(binary);
  });
}

/**
 * Insert a fragment from base64-encoded OOXML at the current cursor position.
 */
export async function insertFragmentFromBase64(base64: string): Promise<void> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    // Mirror the UTF-8 → base64 encoding used by getSelectionAsBase64:
    // base64 → binary string → UTF-8 bytes → decoded string. A plain `atob`
    // would hand Word latin-1 chars for any non-ASCII content and the
    // insert silently no-ops.
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < bytes.length; i++) bytes[i] = binary.charCodeAt(i);
    const ooxml = new TextDecoder('utf-8').decode(bytes);
    selection.insertOoxml(ooxml, Word.InsertLocation.after);
    await context.sync();
  });
}

/**
 * Gets the document as a Blob (for multipart FormData uploads).
 * Reads the compressed file directly as byte arrays — no base64 intermediate.
 */
export async function getDocumentAsBlob(): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      Office.context.document.getFileAsync(
        Office.FileType.Compressed,
        { sliceSize: 65536 },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            reject(new Error(result.error.message));
            return;
          }
          const file = result.value;
          const sliceCount = file.sliceCount;
          const sliceParts: any[] = [];
          let slicesReceived = 0;

          if (sliceCount === 0) {
            file.closeAsync();
            resolve(new Blob([], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }));
            return;
          }

          function getSlice(index: number) {
            file.getSliceAsync(index, (sliceResult) => {
              if (sliceResult.status === Office.AsyncResultStatus.Failed) {
                file.closeAsync();
                reject(new Error(sliceResult.error.message));
                return;
              }
              sliceParts[index] = sliceResult.value.data;
              slicesReceived++;

              if (slicesReceived === sliceCount) {
                file.closeAsync();
                // Build blob directly from slice data
                const parts: Uint8Array[] = sliceParts.map((data: any) => {
                  if (data instanceof Uint8Array) return data;
                  if (data instanceof ArrayBuffer) return new Uint8Array(data);
                  if (Array.isArray(data)) return new Uint8Array(data);
                  // String data — convert char codes to bytes
                  if (typeof data === 'string') {
                    const arr = new Uint8Array(data.length);
                    for (let i = 0; i < data.length; i++) arr[i] = data.charCodeAt(i);
                    return arr;
                  }
                  return new Uint8Array(0);
                });
                resolve(new Blob(parts, { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }));
              } else {
                getSlice(index + 1);
              }
            });
          }
          getSlice(0);
        }
      );
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
}

// Load document from base64 (replaces document body content).
//
// Uses the document-level `insertFileFromBase64` (Word.Document, API set
// 1.5+) rather than the body-level variant. The two differ meaningfully:
//
//   • body.insertFileFromBase64(base64, location)
//       → merges content INTO the host doc's existing section. Page
//         setup / margins / headers / footers come from the HOST, not
//         the source. Floating shapes with absolute coordinates (like
//         Bond Status Report's drawn-line form underlines) end up
//         positioned against the wrong page geometry → off-screen or
//         clipped. Per Office.js docs: "shapes might not import as
//         expected" — that's referring to this body-level variant.
//
//   • document.insertFileFromBase64(base64, location, options)
//       → returns a SectionCollection because it imports the source's
//         SECTION STRUCTURE (page size, margins, headers, footers) AND
//         the inline content. With all `import*` flags on, styles,
//         theme, custom XML parts, paragraph spacing, etc. travel too.
//         Absolute-positioned shapes resolve against the imported
//         section's page geometry, so they land where they were drawn.
//
// We pass every import flag so any well-formed template looks identical
// to its source .docx regardless of how the host (Document1) was set up.
export async function loadDocumentFromBase64(base64: string): Promise<void> {
  return Word.run(async (context) => {
    context.document.insertFileFromBase64(
      base64,
      Word.InsertLocation.replace,
      {
        importChangeTrackingMode: true,
        importCustomProperties: true,
        importCustomXmlParts: true,
        importDifferentOddEvenPages: true,
        importPageColor: true,
        importParagraphSpacing: true,
        importStyles: true,
        importTheme: true,
      } as Word.InsertFileOptions,
    );
    await context.sync();
  });
}

/**
 * Download a docx to the user's machine so they can open it in Word natively.
 * Used by Open Template and Import as a non-mangling alternative to
 * loadDocumentFromBase64 (which calls body.insertFileFromBase64; that strips
 * or relocates absolutely-positioned floating shapes — templates built from
 * the add-in's DocuSign-style placeholder boxes). Native Word renders those
 * shapes faithfully, so the trade-off is one extra user click in exchange
 * for perfect layout fidelity for every template.
 */
export function downloadDocxFile(base64: string, fileName: string): void {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Delete a content control by tag
export async function deleteContentControl(tag: string): Promise<boolean> {
  return Word.run(async (context) => {
    const controls = context.document.contentControls;
    controls.load('items/tag');
    await context.sync();
    let deleted = false;
    for (const cc of controls.items) {
      if (cc.tag === tag) {
        cc.delete(false); // false = keep content
        deleted = true;
        break;
      }
    }
    await context.sync();
    return deleted;
  });
}

// Navigate to / select a content control by tag
export async function selectContentControl(tag: string): Promise<boolean> {
  return Word.run(async (context) => {
    const controls = context.document.contentControls;
    controls.load('items/tag');
    await context.sync();
    for (const cc of controls.items) {
      if (cc.tag === tag) {
        cc.select(Word.SelectionMode.select);
        await context.sync();
        return true;
      }
    }
    return false;
  });
}

// Get custom document properties
export async function getDocumentProperties(): Promise<Record<string, string>> {
  return Word.run(async (context) => {
    const props = context.document.properties.customProperties;
    props.load('items/key,items/value');
    await context.sync();
    const result: Record<string, string> = {};
    for (const prop of props.items) {
      result[prop.key] = String(prop.value);
    }
    return result;
  });
}

// Insert placeholder text like {key} at cursor position
export async function insertPlaceholderText(key: string): Promise<void> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.insertText(`{${key}}`, Word.InsertLocation.replace);
    await context.sync();
  });
}

// Set a custom document property
export async function setDocumentProperty(key: string, value: string): Promise<void> {
  return Word.run(async (context) => {
    context.document.properties.customProperties.add(key, value);
    await context.sync();
  });
}

/**
 * Flag the current document so the Artemis taskpane auto-opens every time
 * the file is reopened — including when it's opened directly from the
 * desktop (File Explorer / Outlook / OneDrive), where Office would
 * otherwise leave the pane closed.
 *
 * Why this matters
 * ----------------
 * The whole binding-safety net (BindingBanner, verify-binding, Protected-
 * View warning) is invisible until the taskpane is open. Without this
 * flag a user can open a template from disk, edit it, press Ctrl+S
 * (writes the local file only), and never learn the change didn't reach
 * Artemis. Setting Office.AutoShowTaskpaneWithDocument persists a flag
 * INSIDE the .docx; on the next open Office reads it and shows the pane.
 *
 * Scope / side-effects
 * --------------------
 * - Sticky: persists in the file; every later open auto-shows the pane.
 *   That's the intended behaviour for a template authoring file.
 * - The flag would normally ride into documents generated from this
 *   template; the doc-service strips it at generation time so generated
 *   bonds don't auto-open the pane.
 * - Best-effort: settings.saveAsync failures are swallowed — a missing
 *   auto-show is a minor UX regression, never a reason to fail a save.
 */
export async function enableAutoShowTaskpane(): Promise<void> {
  return new Promise<void>((resolve) => {
    try {
      const settings = Office.context.document.settings;
      settings.set('Office.AutoShowTaskpaneWithDocument', true);
      settings.saveAsync(() => resolve());
    } catch {
      resolve();
    }
  });
}

/**
 * Set the color of all content controls matching a given tag.
 */
export async function setContentControlColor(tag: string, color: string): Promise<void> {
  return Word.run(async (context) => {
    const controls = context.document.body.contentControls;
    controls.load('items');
    await context.sync();
    for (const cc of controls.items) {
      cc.load('tag');
    }
    await context.sync();
    for (const cc of controls.items) {
      if (cc.tag === tag) {
        cc.color = color;
      }
    }
    await context.sync();
  });
}

/**
 * Get all IF: and UNLESS: conditional tags from the document with their nesting level.
 */
/**
 * Open a base64-encoded DOCX as a new Word document window.
 */
export async function createNewDocumentFromBase64(base64: string): Promise<void> {
  const doc = await Word.createDocument(base64);
  await doc.open();
}

export async function getConditionalTags(): Promise<Array<{ tag: string; key: string; type: 'IF' | 'UNLESS'; level: number }>> {
  return Word.run(async (context) => {
    const controls = context.document.body.contentControls;
    controls.load('items');
    await context.sync();
    const tags: Array<{ tag: string; key: string; type: 'IF' | 'UNLESS'; level: number }> = [];
    for (const cc of controls.items) {
      cc.load('tag,parentContentControlOrNullObject');
    }
    await context.sync();
    for (const cc of controls.items) {
      if (cc.tag?.startsWith('IF:')) {
        tags.push({ tag: cc.tag, key: cc.tag.substring(3), type: 'IF', level: 0 });
      } else if (cc.tag?.startsWith('UNLESS:')) {
        tags.push({ tag: cc.tag, key: cc.tag.substring(7), type: 'UNLESS', level: 0 });
      }
    }
    return tags;
  });
}
